import { Button, Flex, Form, Input, InputNumber, Switch } from "antd";
import { useEffect } from "react";
import {
  useCreateCategoriesMutation,
  useUpdateCategoriesMutation,
} from "../../../../../app/features/admin/admin.api";
import type { Category } from "../../../../../app/features/admin/admin.types";
import CustomModal from "../../../../../components/CustomModal";
import type { ModalType } from "../../../../../hooks/useModal";
import { toast } from "../../../../../lib/toast";
import { slug } from "../../../../../utils/string.types";

interface CategoryModalProps {
  open: boolean;
  onCancel: () => void;
  initialValues?: Category | null;
  type?: ModalType["type"];
}

const CategoryModal = ({
  open,
  onCancel,
  initialValues,
  type,
}: CategoryModalProps) => {
  const [form] = Form.useForm();
  const [createCategory, { isLoading: isCreating }] =
    useCreateCategoriesMutation();
  const [updateCategory, { isLoading: isUpdating }] =
    useUpdateCategoriesMutation();

  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (initialValues && type === "UPDATE") {
      form.setFieldsValue({
        name: initialValues.name,
        sortOrder: initialValues.sortOrder,
        isActive: initialValues.isActive,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        isActive: true,
        sortOrder: 0,
      });
    }
  }, [initialValues, form, open, type]);

  const handleSubmit = async (values: any) => {
    try {
      let res;
      if (initialValues?.id && type === "UPDATE") {
        res = await updateCategory({
          id: initialValues.id,
          ...values,
          slug: initialValues?.slug || slug(values.name),
        }).unwrap();
      } else {
        res = await createCategory({
          ...values,
          slug: slug(values.name),
        }).unwrap();
      }

      // Since generic response doesn't always have success displayed in toast by default unless handled
      // But checking admin.api logic, RTK Query mutation usually triggers toast if setup globally or we do it here.
      // Based on previous code practices I'll assume we might need to show toast manually or just close

      // Let's use showResponseToast if available or simple check
      if (res) {
        toast(
          "success",
          type === "UPDATE"
            ? "Category updated successfully"
            : "Category created successfully",
        );
        onCancel();
      }
    } catch (error: any) {
      toast("error", error?.data?.message || "Something went wrong");
    }
  };

  return (
    <CustomModal
      handleClose={onCancel}
      title={type === "UPDATE" ? "Update Category" : "Create Category"}
      open={open}
      width={500}
      footer
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ isActive: true, sortOrder: 0 }}
        className="custom-form"
        size="large"
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: "Please enter category name" }]}
        >
          <Input placeholder="Enter category name" />
        </Form.Item>

        <Form.Item
          label="Sort Order"
          name="sortOrder"
          rules={[{ required: true, message: "Please enter sort order" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            placeholder="Enter sort order"
          />
        </Form.Item>

        <Form.Item label="Status" name="isActive" valuePropName="checked">
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>

        <Flex justify="end" gap={8}>
          <Button
            className="min-w-[120px]"
            disabled={isLoading}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="min-w-[120px]"
            htmlType="submit"
            loading={isLoading}
          >
            {type === "UPDATE" ? "Update" : "Create"}
          </Button>
        </Flex>
      </Form>
    </CustomModal>
  );
};

export default CategoryModal;
